import React, { useState, useMemo, useEffect } from "react";
import { supabase, displayDate, displayTime, asFlatNumber } from "./supabaseClient";
import {
  LayoutGrid, Camera, Scan, UserCheck, Users, Wallet, MessageSquareWarning,
  Megaphone, Search, Bell, Check, X, ShieldCheck, Plus, Filter,
  Phone, Car, Package, Wrench, UserCog, Building2, KeyRound, Receipt,
  LogOut, Trash2, ArrowRight, ChevronsLeft, ChevronsRight, Landmark, TrendingUp, TrendingDown,
} from "lucide-react";

/* =========================================================
   DESIGN TOKENS — shared with Login.js
========================================================= */
export const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:ital,wght@0,500;0,600;0,700;1,600;1,700&display=swap');`;

export const T = {
  ink: "#14162E", cream: "#F8EFDC", indigo: "#4E5AA6", yellow: "#F6B93B",
  cyan: "#A9E4DE", mint: "#C9E7DC", red: "#E2483A", green: "#2F9E52",
  white: "#FFFDF7", muted: "#5B6270",
};
export const dotBg = { backgroundImage: `radial-gradient(rgba(20,22,46,0.14) 1.3px, transparent 1.3px)`, backgroundSize: "18px 18px" };
export const mono = "'IBM Plex Mono', monospace";
export const sans = "'Space Grotesk', sans-serif";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "gateai", label: "Gate AI", icon: Camera },
  { id: "visitors", label: "Visitors", icon: UserCheck },
  { id: "residents", label: "Residents", icon: Users },
  { id: "billing", label: "Billing & Dues", icon: Wallet },
  { id: "accounts", label: "Accounts & Expenses", icon: Landmark },
  { id: "complaints", label: "Complaints", icon: MessageSquareWarning },
  { id: "maintenance", label: "Maintenance Log", icon: Wrench },
  { id: "staff", label: "Staff & Roles", icon: UserCog },
  { id: "amenities", label: "Venues & Amenities", icon: Building2 },
  { id: "codes", label: "User Codes", icon: KeyRound },
  { id: "announcements", label: "Announcements", icon: Megaphone },
];

const INTEREST_RATE_ANNUAL = 0.21;
const GST_THRESHOLD = 7500;
const EXPENSE_CATEGORIES = ["Security salaries", "Housekeeping", "Electricity", "Water", "Lift AMC", "Repairs & maintenance", "Landscaping", "Insurance", "Admin & office", "Other"];
const OPENING_BALANCE = 185000;

const rid = () => Math.random().toString(36).slice(2, 8).toUpperCase();

/* ---------------- seed data (a fresh society starts here) ---------------- */
const SEED = {
  gateAIFeed: [
    { id: 1, kind: "face", label: "Ramesh Kumar", sub: "A-101 · Resident", confidence: 98, time: "9:41 AM", match: true, flat: "A-101" },
    { id: 2, kind: "anpr", label: "MP09 CD 4521", sub: "B-204 · Registered vehicle", confidence: 95, time: "9:38 AM", match: true, flat: "B-204" },
    { id: 3, kind: "face", label: "Unrecognized face", sub: "No enrollment match", confidence: 41, time: "9:35 AM", match: false, flat: "Gate 1" },
    { id: 4, kind: "anpr", label: "KA05 AB1234", sub: "Not on any flat's list", confidence: 88, time: "9:34 AM", match: false, flat: "Gate 1" },
  ],
  visitorLog: [
    { id: rid(), name: "Priya Nair", flat: "C-302", purpose: "Guest", entry: "9:15 AM", exit: "—", status: "In" },
    { id: rid(), name: "Sunil Electrician", flat: "D-410", purpose: "Service", entry: "8:55 AM", exit: "10:02 AM", status: "Out" },
  ],
  residents: [
    { flat: "A-101", name: "Ramesh Kumar", type: "Owner", contact: "98xxxxxx12", plate: "MP09 CD 4521", face: "Enrolled", consent: "Given" },
    { flat: "A-108", name: "Fatima Sheikh", type: "Tenant", contact: "97xxxxxx45", plate: "—", face: "Not enrolled", consent: "Pending" },
    { flat: "B-204", name: "Arjun Mehta", type: "Owner", contact: "99xxxxxx87", plate: "MP09 XY 7712", face: "Enrolled", consent: "Given" },
    { flat: "C-302", name: "Priya Nair", type: "Tenant", contact: "96xxxxxx23", plate: "—", face: "Enrolled", consent: "Given" },
    { flat: "D-410", name: "Suresh Iyer", type: "Owner", contact: "98xxxxxx61", plate: "MP09 LK 3390", face: "Not enrolled", consent: "Pending" },
  ],
  dues: [
    { flat: "A-101", month: "Aug 2026", amount: 2400, status: "Paid", receipt: "RCPT-2291", daysOverdue: 0 },
    { flat: "A-108", month: "Aug 2026", amount: 2400, status: "Overdue", receipt: "—", daysOverdue: 12 },
    { flat: "B-204", month: "Aug 2026", amount: 2400, status: "Paid", receipt: "RCPT-2292", daysOverdue: 0 },
    { flat: "C-302", month: "Aug 2026", amount: 2200, status: "Pending", receipt: "—", daysOverdue: 0 },
    { flat: "D-410", month: "Aug 2026", amount: 2600, status: "Paid", receipt: "RCPT-2293", daysOverdue: 0 },
  ],
  expenses: [
    { id: rid(), date: "Aug 3, 2026", category: "Security salaries", vendor: "Guard staff (3)", amount: 54000, mode: "Bank transfer", note: "August salaries" },
    { id: rid(), date: "Aug 7, 2026", category: "Electricity", vendor: "State Electricity Board", amount: 22400, mode: "Online", note: "Common area + lift power" },
    { id: rid(), date: "Aug 12, 2026", category: "Housekeeping", vendor: "Kamla Devi & team", amount: 18000, mode: "Cash", note: "Monthly housekeeping" },
    { id: rid(), date: "Aug 18, 2026", category: "Lift AMC", vendor: "Vikram — Lift AMC vendor", amount: 9500, mode: "Cheque", note: "Quarterly AMC visit" },
  ],
  complaints: [
    { id: "#118", title: "Lift in Wing B not working", flat: "B-204", priority: "High", assignee: "Vikram — Lift AMC vendor", stage: 2 },
    { id: "#117", title: "Water pressure low, 4th floor", flat: "D-410", priority: "Medium", assignee: "Not yet assigned", stage: 0 },
    { id: "#116", title: "Streetlight near Gate 2 out", flat: "Common area", priority: "Low", assignee: "Rajesh — Electrician", stage: 1 },
  ],
  maintenanceLog: [
    { asset: "CCTV Cameras", lastDone: "Jun 2, 2026", nextDue: "Dec 2, 2026", status: "OK" },
    { asset: "Building Painting", lastDone: "Jan 15, 2025", nextDue: "Jan 15, 2027", status: "OK" },
    { asset: "Plumbing — Wing B", lastDone: "Jul 20, 2026", nextDue: "Sep 20, 2026", status: "Due soon" },
    { asset: "Sewage Line Cleaning", lastDone: "Mar 10, 2026", nextDue: "Aug 10, 2026", status: "Overdue" },
  ],
  staffRoles: [
    { name: "Kamla Devi", type: "Maid", flat: "A-101, A-108", status: "Approved", role: "Housekeeping" },
    { name: "Rajesh Yadav", type: "Electrician", flat: "Common area", status: "Pending", role: "—" },
    { name: "Meena Kumari", type: "Maid", flat: "C-302", status: "Pending", role: "—" },
  ],
  userCodes: [
    { code: "SNR-8841", role: "Resident", flat: "B-204", status: "Active" },
    { code: "SNR-2210", role: "Guard", flat: "Gate 1", status: "Used" },
  ],
  venues: ["Clubhouse", "Swimming Pool", "Community Hall"],  amenities: [
    { name: "Clubhouse", requester: "A. Sharma — B-402", slot: "Aug 29, 6–9 PM", payment: "Paid", status: "Pending" },
    { name: "Swimming Pool", requester: "S. Iyer — A-215", slot: "Aug 28, 7–8 AM", payment: "Paid", status: "Approved" },
  ],
  announcements: [
    { title: "Water supply shutdown — Sat 9–11 AM", body: "Maintenance work on the main line. Store water in advance.", date: "Aug 24" },
    { title: "AGM scheduled for Sep 5, 7 PM", body: "Community hall. Agenda: annual budget, lift AMC renewal.", date: "Aug 22" },
  ],
};

const ROLE_MAP = { Maid: "Housekeeping", Electrician: "Maintenance", Plumber: "Maintenance", Guard: "Security" };

/* ---------------- shared primitives ---------------- */
function HardCard({ children, style }) {
  return <div style={{ background: T.white, border: `2.5px solid ${T.ink}`, borderRadius: 14, boxShadow: `5px 5px 0 ${T.ink}`, ...style }}>{children}</div>;
}
function StickerPill({ status }) {
  const map = {
    Paid: T.green, Resolved: T.green, In: T.green, cleared: T.green, Given: T.green, Enrolled: T.green,
    Approved: T.green, Active: T.green, OK: T.green,
    Pending: "#B8842B", pending: "#B8842B", "In progress": "#B8842B", Assigned: "#B8842B", "Due soon": "#B8842B", Used: "#B8842B",
    Overdue: T.red, Open: T.red, Expired: T.red,
    Out: T.muted, "Not enrolled": T.muted, Reported: T.muted, Waived: T.muted,
  };
  const fg = map[status] || T.muted;
  return (
    <span style={{ border: `2px solid ${fg}`, color: fg, fontSize: 11, fontWeight: 700, padding: "3px 11px", borderRadius: 999, whiteSpace: "nowrap", fontFamily: mono }}>
      {status}
    </span>
  );
}
function Tag({ children, bg }) {
  return <span style={{ display: "inline-block", background: bg || T.yellow, border: `2px solid ${T.ink}`, borderRadius: 999, padding: "3px 12px", fontSize: 10.5, fontWeight: 700, fontFamily: mono, letterSpacing: 0.4 }}>{children}</span>;
}
function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
      <div>
        <h2 style={{ fontFamily: sans, fontSize: 21, fontWeight: 700, color: T.ink, margin: 0 }}>{title}</h2>
        {sub && <p style={{ color: T.muted, fontSize: 13, margin: "5px 0 0" }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}
function HardButton({ children, bg, color, small, style, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: "flex", alignItems: "center", gap: 6, background: disabled ? "#DCD6C2" : (bg || T.yellow), color: color || T.ink,
      border: `2px solid ${T.ink}`, borderRadius: 9, padding: small ? "7px 12px" : "10px 16px", fontSize: small ? 12 : 13,
      fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", boxShadow: disabled ? "none" : `3px 3px 0 ${T.ink}`, fontFamily: sans, ...style,
    }}>
      {children}
    </button>
  );
}
function Toolbar({ placeholder, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: T.white, border: `2.5px solid ${T.ink}`, borderRadius: 10, padding: "9px 14px" }}>
        <Search size={15} color={T.muted} />
        <input placeholder={placeholder} value={value} onChange={onChange} style={{ border: "none", outline: "none", fontSize: 13.5, flex: 1, background: "transparent", color: T.ink, fontFamily: sans }} />
      </div>
    </div>
  );
}
function Table({ columns, rows, renderRow }) {
  return (
    <HardCard style={{ overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
        <thead>
          <tr style={{ background: T.cream }}>
            {columns.map((c) => (
              <th key={c} style={{ textAlign: "left", padding: "12px 16px", fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: 0.4, borderBottom: `2.5px solid ${T.ink}`, fontFamily: mono, whiteSpace: "nowrap" }}>
                {c.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </HardCard>
  );
}
function InlineAddRow({ fields, onAdd, buttonLabel }) {
  const [vals, setVals] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.type === "select" ? (f.options?.[0] || "") : ""])));
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const canAdd = fields.every((f) => !f.required || String(vals[f.key] || "").trim());
  return (
    <HardCard style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14, background: T.mint }}>
      {fields.map((f) =>
        f.type === "select" ? (
          <select key={f.key} value={vals[f.key]} onChange={(e) => set(f.key, e.target.value)}
            style={{ border: `2px solid ${T.ink}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: sans, minWidth: 160, background: T.white }}>
            {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input key={f.key} type={f.type || "text"} placeholder={f.label} value={vals[f.key]} onChange={(e) => set(f.key, e.target.value)}
            style={{ border: `2px solid ${T.ink}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: sans, minWidth: 140, flex: f.flex || "none" }} />
        )
      )}
      <HardButton small bg={T.green} color={T.white} disabled={!canAdd} onClick={() => { onAdd(vals); setVals(Object.fromEntries(fields.map((f) => [f.key, f.type === "select" ? (f.options?.[0] || "") : ""]))); }}>
        <Plus size={13} /> {buttonLabel}
      </HardButton>
    </HardCard>
  );
}
const td = { padding: "12px 16px", fontSize: 13.5, color: T.ink, borderBottom: `1.5px solid #EDE4CE`, whiteSpace: "nowrap" };

/* =========================================================
   MAIN ERP
========================================================= */
export default function ERP({ session, onLogout }) {
  const [active, setActive] = useState("overview");
  const [plan, setPlan] = useState(session?.plan || "399");
  const societyId = session?.societyId;
  const societyName = session?.societyName || "Your Society";
  const adminName = session?.adminName || "Admin";
  const initials = adminName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const [gateAIFeed, setGateAIFeed] = useState([]);
  const [visitorLog, setVisitorLog] = useState([]);
  const [residents, setResidents] = useState([]);
  const [dues, setDues] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [maintenanceLog, setMaintenanceLog] = useState([]);
  const [staffRoles, setStaffRoles] = useState([]);
  const [userCodes, setUserCodes] = useState([]);
  const [venues, setVenues] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!societyId) return;
    let alive = true;
    const load = async () => {
      setLoading(true);
      const q = (table, select = "*") => supabase.from(table).select(select).eq("society_id", societyId);
      const [g, v, r, d, c, m, st, codes, a, b, n, ex] = await Promise.all([
        q("gate_events", "*, units(flat_number)").eq("review_status", "pending").order("occurred_at", { ascending: false }),
        q("visitors", "*, units(flat_number)").order("entry_at", { ascending: false }),
        q("residents", "*, units(flat_number), vehicles(plate_number)"),
        q("dues", "*, units(flat_number), billing_cycles(label)"),
        q("complaints", "*, units(flat_number)").order("created_at", { ascending: false }),
        q("maintenance_logs").order("created_at", { ascending: false }),
        q("staff").order("created_at", { ascending: false }),
        q("onboarding_codes").order("created_at", { ascending: false }),
        q("amenities").eq("active", true),
        q("amenity_bookings", "*, amenities(name), units(flat_number)").order("created_at", { ascending: false }),
        q("announcements").order("published_at", { ascending: false }),
        q("expenses").order("expense_date", { ascending: false }),
      ]);
      if (!alive) return;
      setGateAIFeed((g.data || []).map(x => ({ id:x.id, kind:x.kind, label:x.label, sub:x.description || (x.units ? `${x.units.flat_number}` : x.gate_name), confidence:Number(x.confidence || 0), time:displayTime(x.occurred_at), match:x.matched, flat:asFlatNumber(x.units), dbId:x.id })));
      setVisitorLog((v.data || []).map(x => ({ id:x.id, name:x.name, flat:asFlatNumber(x.units), purpose:x.purpose, entry:displayTime(x.entry_at), exit:x.exit_at ? displayTime(x.exit_at) : "—", status:x.status === "in" ? "In" : "Out" })));
      setResidents((r.data || []).map(x => ({ id:x.id, flat:asFlatNumber(x.units), name:x.full_name, type:x.person_type === "owner" ? "Owner" : "Tenant", contact:x.contact || "—", plate:x.vehicles?.[0]?.plate_number || "—", face:x.face_status === "enrolled" ? "Enrolled" : "Not enrolled", consent:x.biometric_consent === "given" ? "Given" : "Pending", unitId:x.unit_id })));
      setDues((d.data || []).map(x => ({ id:x.id, flat:asFlatNumber(x.units), month:x.billing_cycles?.label || "Current cycle", amount:Number(x.base_amount), status:x.status[0].toUpperCase()+x.status.slice(1), receipt:x.receipt_number || "—", daysOverdue:x.days_overdue })));
      setComplaints((c.data || []).map(x => ({ id:x.id, dbId:x.id, title:x.title, flat:asFlatNumber(x.units), priority:x.priority[0].toUpperCase()+x.priority.slice(1), assignee:x.assignee || "Not yet assigned", stage:["reported","assigned","in_progress","resolved"].indexOf(x.stage) })));
      setMaintenanceLog((m.data || []).map(x => ({ id:x.id, asset:x.asset_name, lastDone:displayDate(x.last_done), nextDue:x.next_due ? displayDate(x.next_due) : "TBD", status:x.status })));
      setStaffRoles((st.data || []).map(x => ({ id:x.id, name:x.full_name, type:x.staff_type, flat:x.linked_area || "—", status:x.status[0].toUpperCase()+x.status.slice(1), role:x.role_name || "—" })));
      setUserCodes((codes.data || []).map(x => ({ id:x.id, code:x.code, role:x.role[0].toUpperCase()+x.role.slice(1), flat:asFlatNumber(x.unit_id ? {flat_number:x.unit_id} : null), status:x.status[0].toUpperCase()+x.status.slice(1) })));
      setVenues((a.data || []).map(x => x.name));
      setAmenities((b.data || []).map(x => ({ id:x.id, name:x.amenities?.name || "Amenity", requester:x.requester_name, slot:`${displayDate(x.starts_at)}, ${displayTime(x.starts_at)}–${displayTime(x.ends_at)}`, payment:x.payment_status[0].toUpperCase()+x.payment_status.slice(1), status:x.status[0].toUpperCase()+x.status.slice(1) })));
      setAnnouncements((n.data || []).map(x => ({ id:x.id, title:x.title, body:x.body || "", date:displayDate(x.published_at) })));
      setExpenses((ex.data || []).map(x => ({ id:x.id, date:displayDate(x.expense_date), category:x.category, vendor:x.vendor, amount:Number(x.amount), mode:x.payment_method.replace("_", " "), note:x.note || "" })));
      setLoading(false);
    };
    load();
    return () => { alive = false; };
  }, [societyId]);

  /* ---------- derived numbers, computed live from state ---------- */
  const billingRows = useMemo(() => dues.map((d) => {
    const interest = d.status === "Overdue" ? Math.round(d.amount * (INTEREST_RATE_ANNUAL / 365) * d.daysOverdue) : 0;
    const gstApplicable = d.amount > GST_THRESHOLD;
    const gst = gstApplicable ? Math.round((d.amount + interest) * 0.18) : 0;
    return { ...d, interest, gst, gstApplicable, total: d.amount + interest + gst };
  }), [dues]);

  const totalExpenses = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);
  const expensesByCategory = useMemo(() => {
    const map = {};
    expenses.forEach((e) => { map[e.category] = (map[e.category] || 0) + e.amount; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  const stats = useMemo(() => {
    const totalDue = billingRows.reduce((s, d) => s + d.total, 0);
    const totalCollected = billingRows.filter((d) => d.status === "Paid").reduce((s, d) => s + d.total, 0);
    const pct = totalDue ? Math.round((totalCollected / totalDue) * 100) : 0;
    const openComplaints = complaints.filter((c) => c.stage < 3).length;
    const highOpen = complaints.filter((c) => c.stage < 3 && c.priority === "High").length;
    const pendingVisitors = gateAIFeed.length;
    const netBalance = OPENING_BALANCE + totalCollected - totalExpenses;
    return {
      totalFlats: residents.length,
      pct, totalCollected, totalDue,
      openComplaints, highOpen,
      visitorsToday: visitorLog.length + gateAIFeed.length,
      pendingVisitors,
      totalExpenses, netBalance,
    };
  }, [billingRows, complaints, residents, visitorLog, gateAIFeed, totalExpenses]);

  /* ---------- actions ---------- */
  const approveGate = async (item) => {
    await supabase.from("gate_events").update({ review_status:"approved", reviewed_by:session?.userId, reviewed_at:new Date().toISOString() }).eq("id", item.dbId || item.id);
    const { data: event } = await supabase.from("gate_events").select("*").eq("id", item.dbId || item.id).maybeSingle();
    if (event) await supabase.from("visitors").insert({ society_id:societyId, name:item.label, purpose:item.kind === "anpr" ? "Vehicle" : "Face entry", gate_event_id:event.id, created_by:session?.userId });
    setGateAIFeed((f) => f.filter((g) => g.id !== item.id));
  };
  const rejectGate = async (item) => { await supabase.from("gate_events").update({ review_status:"rejected", reviewed_by:session?.userId, reviewed_at:new Date().toISOString() }).eq("id", item.dbId || item.id); setGateAIFeed((f) => f.filter((g) => g.id !== item.id)); };
  const logExit = async (id) => { await supabase.from("visitors").update({ exit_at:new Date().toISOString(), status:"out" }).eq("id", id); setVisitorLog((v) => v.map((row) => (row.id === id ? { ...row, exit: "Just now", status: "Out" } : row))); };

  const recordReceipt = async (flat) => {
    const row = dues.find(x => x.flat === flat && x.status !== "Paid");
    if (!row) return;
    await supabase.rpc("record_due_payment", { p_due_id:row.id, p_amount:row.amount, p_method:"online" });
    setDues((d) => d.map((x) => (x.id === row.id ? { ...x, status: "Paid", receipt: "Recorded", daysOverdue: 0 } : x)));
  };
  const generateNextCycle = async () => {
    const months = dues.map((d) => d.month);
    const latest = months[months.length - 1] || "Aug 2026";
    const [, y] = latest.split(" ");
    const nextLabel = latest.startsWith("Aug") ? `Sep ${y}` : `${latest.split(" ")[0]} ${Number(y) + 1}`;
    if (dues.some((d) => d.month === nextLabel)) return;
    const { data: cycle } = await supabase.from("billing_cycles").insert({ society_id:societyId, label:nextLabel }).select().single();
    if (!cycle) return;
    const rows = residents.filter(r => r.unitId).map(r => ({ society_id:societyId, cycle_id:cycle.id, unit_id:r.unitId, base_amount:2400, status:"pending" }));
    if (rows.length) {
      const { data } = await supabase.from("dues").insert(rows).select("*, units(flat_number), billing_cycles(label)");
      if (data) setDues(d => [...d, ...data.map(x => ({ id:x.id, flat:asFlatNumber(x.units), month:x.billing_cycles.label, amount:Number(x.base_amount), status:"Pending", receipt:"—", daysOverdue:0 }))]);
    }
  };

  const advanceComplaint = async (id) => { const c = complaints.find(x => x.id === id); if (!c || c.stage >= 3) return; const next=["reported","assigned","in_progress","resolved"][c.stage+1]; await supabase.from("complaints").update({ stage:next, assignee:c.stage===0 ? "Committee — assigning vendor" : c.assignee, updated_at:new Date().toISOString() }).eq("id", c.dbId || id); setComplaints(cs => cs.map(x => x.id===id ? {...x, stage:x.stage+1, assignee:x.stage===0 ? "Committee — assigning vendor" : x.assignee} : x)); };
  const addComplaint = async (vals) => { const { data } = await supabase.from("complaints").insert({ society_id:societyId, title:vals.title, priority:(vals.priority || "medium").toLowerCase(), assignee:"Not yet assigned", created_by:session?.userId }).select("*, units(flat_number)").single(); if (data) setComplaints(cs => [{ id:data.id, dbId:data.id, title:data.title, flat:vals.flat || "Common area", priority:data.priority[0].toUpperCase()+data.priority.slice(1), assignee:data.assignee, stage:0 }, ...cs]); };

  const addMaintenance = async (vals) => { const { data } = await supabase.from("maintenance_logs").insert({ society_id:societyId, asset_name:vals.asset, next_due:vals.nextDue || null, created_by:session?.userId }).select().single(); if (data) setMaintenanceLog(m => [{ id:data.id, asset:data.asset_name, lastDone:"Today", nextDue:data.next_due || "TBD", status:data.status }, ...m]); };

  const approveStaff = async (name) => { const row=staffRoles.find(x=>x.name===name); if (!row) return; const role=ROLE_MAP[row.type] || "General"; await supabase.from("staff").update({ status:"approved", role_name:role, approved_at:new Date().toISOString(), approved_by:session?.userId }).eq("id",row.id); setStaffRoles(s => s.map(x=>x.name===name ? {...x,status:"Approved",role} : x)); };
  const addStaff = async (vals) => { const { data }=await supabase.from("staff").insert({ society_id:societyId, full_name:vals.name, staff_type:vals.type||"Staff", linked_area:vals.flat||null }).select().single(); if(data) setStaffRoles(s=>[...s,{id:data.id,name:data.full_name,type:data.staff_type,flat:data.linked_area||"—",status:"Pending",role:"—"}]); };

  const generateCode = async () => { const { data }=await supabase.rpc("generate_onboarding_code",{p_society_id:societyId,p_role:"resident"}); if(data) setUserCodes(c=>[{id:data.id,code:data.code,role:"Resident",flat:"—",status:"Active"},...c]); };

  const addVenue = async (nameVal) => { const v=nameVal.trim(); if(!v || venues.includes(v)) return; const {data}=await supabase.from("amenities").insert({society_id:societyId,name:v}).select().single(); if(data) setVenues(vs=>[...vs,v]); };
  const removeVenue = async (v) => { await supabase.from("amenities").update({active:false}).eq("society_id",societyId).eq("name",v); setVenues(vs=>vs.filter(x=>x!==v)); };
  const approveAmenity = async (i) => { const row=amenities[i]; if(!row) return; await supabase.from("amenity_bookings").update({status:"approved"}).eq("id",row.id); setAmenities(a=>a.map((x,idx)=>idx===i?{...x,status:"Approved"}:x)); };

  const addAnnouncement = async (vals) => { const {data}=await supabase.from("announcements").insert({society_id:societyId,title:vals.title,body:vals.body||"",created_by:session?.userId}).select().single(); if(data) setAnnouncements(a=>[{id:data.id,title:data.title,body:data.body,date:"Today"},...a]); };

  const addExpense = async (vals) => { const amt=Math.round(Number(vals.amount)); if(!amt || amt<=0) return; const method=(vals.mode||"cash").toLowerCase().replace(" ","_"); const {data}=await supabase.from("expenses").insert({society_id:societyId,category:vals.category||"Other",vendor:vals.vendor||"—",amount:amt,payment_method:method,note:vals.note||"",created_by:session?.userId}).select().single(); if(data) setExpenses(ex=>[{id:data.id,date:"Today",category:data.category,vendor:data.vendor,amount:Number(data.amount),mode:data.payment_method.replace("_"," "),note:data.note||""},...ex]); };
  const removeExpense = async (id) => { await supabase.from("expenses").delete().eq("id",id); setExpenses(ex=>ex.filter(e=>e.id!==id)); };

  const addResident = async (vals) => {
    const { data: unit } = await supabase.from("units").upsert({ society_id:societyId, flat_number:vals.flat.trim() }, { onConflict:"society_id,flat_number" }).select().single();
    if (!unit) return;
    const { data } = await supabase.from("residents").insert({ society_id:societyId, unit_id:unit.id, full_name:vals.name.trim(), person_type:(vals.type || "Owner").toLowerCase() }).select("*, units(flat_number)").single();
    if (data) setResidents(r => [{ id:data.id, flat:asFlatNumber(data.units), name:data.full_name, type:data.person_type[0].toUpperCase()+data.person_type.slice(1), contact:"—", plate:"—", face:"Not enrolled", consent:"Pending", unitId:data.unit_id }, ...r]);
  };
  const changePlan = async () => {
    const { error } = await supabase.from("societies").update({ plan:"499" }).eq("id",societyId);
    if (!error) setPlan("499");
  };

  const filteredVisitors = visitorLog.filter((v) => (v.name + v.flat).toLowerCase().includes(search.toLowerCase()));
  const filteredResidents = residents.filter((r) => (r.name + r.flat).toLowerCase().includes(search.toLowerCase()));

  /* ---------- screens ---------- */
  function Overview() {
    const cards = [
      { label: "Total flats", value: String(stats.totalFlats), note: `${residents.length} on directory`, icon: Users },
      { label: "Dues collected", value: `${stats.pct}%`, note: `₹${(stats.totalCollected / 1000).toFixed(1)}K of ₹${(stats.totalDue / 1000).toFixed(1)}K`, icon: Wallet },
      { label: "Open complaints", value: String(stats.openComplaints), note: `${stats.highOpen} high priority`, icon: MessageSquareWarning },
      { label: "Awaiting gate review", value: String(stats.pendingVisitors), note: `${visitorLog.length} logged today`, icon: UserCheck },
    ];
    return (
      <div>
        {loading && <HardCard style={{ padding: 14, marginBottom: 16, background: T.cyan }}>Loading live society data…</HardCard>}
        <SectionHeader title={`${societyName} — Overview`} sub="Live numbers, computed from what's actually recorded below" />
        {plan === "399" && (
          <HardCard style={{ padding: "12px 16px", marginBottom: 16, background: T.cream, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5 }}>Sponsored placement — you're on the ₹399 plan. Upgrade to ₹499 to remove ads.</span>
            <HardButton small onClick={changePlan}>Upgrade to ₹499 <ArrowRight size={12} /></HardButton>
          </HardCard>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, marginBottom: 18 }}>
          {cards.map((s) => (
            <HardCard key={s.label} style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: T.muted, fontSize: 10.5, fontWeight: 700, fontFamily: mono }}>{s.label.toUpperCase()}</span>
                <s.icon size={15} color={T.indigo} />
              </div>
              <div style={{ fontFamily: mono, fontStyle: "italic", fontSize: 24, fontWeight: 700, color: T.indigo, marginTop: 8 }}>{s.value}</div>
              <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>{s.note}</div>
            </HardCard>
          ))}
        </div>
        <HardCard style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: 99, background: T.green }} />
            <span style={{ fontFamily: sans, fontWeight: 700, fontSize: 14.5 }}>Gate AI — awaiting your review</span>
          </div>
          {gateAIFeed.length === 0 && <div style={{ fontSize: 13, color: T.muted }}>Nothing waiting — every entry has been cleared.</div>}
          {gateAIFeed.slice(0, 3).map((g) => (
            <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "1.5px dashed #E4D9BC" }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: T.cream, border: `2px solid ${T.ink}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {g.kind === "anpr" ? <Car size={13} color={T.ink} /> : <Camera size={13} color={T.ink} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{g.label}</div>
                <div style={{ fontSize: 11.5, color: T.muted }}>{g.sub} · {g.confidence}% confidence</div>
              </div>
              <HardButton small bg={T.green} color={T.white} onClick={() => approveGate(g)}><Check size={12} /></HardButton>
              <HardButton small bg={T.white} color={T.red} onClick={() => rejectGate(g)}><X size={12} /></HardButton>
            </div>
          ))}
        </HardCard>
        <HardCard style={{ padding: 18, marginTop: 16 }}>
          <div style={{ fontFamily: sans, fontWeight: 700, fontSize: 14.5, marginBottom: 12 }}>Financial snapshot</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.green, fontSize: 11, fontWeight: 700, fontFamily: mono }}><TrendingUp size={13} /> COLLECTED</div>
              <div style={{ fontFamily: mono, fontSize: 19, fontWeight: 700, marginTop: 4 }}>₹{stats.totalCollected.toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.red, fontSize: 11, fontWeight: 700, fontFamily: mono }}><TrendingDown size={13} /> EXPENSES</div>
              <div style={{ fontFamily: mono, fontSize: 19, fontWeight: 700, marginTop: 4 }}>₹{stats.totalExpenses.toLocaleString("en-IN")}</div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.indigo, fontSize: 11, fontWeight: 700, fontFamily: mono }}><Landmark size={13} /> BALANCE</div>
              <div style={{ fontFamily: mono, fontSize: 19, fontWeight: 700, marginTop: 4 }}>₹{stats.netBalance.toLocaleString("en-IN")}</div>
            </div>
          </div>
        </HardCard>
      </div>
    );
  }

  function GateAI() {
    return (
      <div>
        <SectionHeader title="Gate AI — Face Recognition & ANPR" sub="Every match is reviewed by the guard before the gate opens" />
        <HardCard style={{ padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, background: T.cyan }}>
          <ShieldCheck size={16} color={T.ink} />
          <span style={{ fontSize: 12.5 }}>No entry is auto-cleared — approving here moves it into today's Visitor Log.</span>
        </HardCard>
        {gateAIFeed.length === 0 && <HardCard style={{ padding: 16, fontSize: 13.5, color: T.muted }}>Queue is empty. New scans will show up here.</HardCard>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {gateAIFeed.map((g) => (
            <HardCard key={g.id} style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: g.match ? "#E4F3E9" : "#FBE3DF", border: `2px solid ${T.ink}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {g.kind === "face" ? <Camera size={15} color={g.match ? T.green : T.red} /> : <Scan size={15} color={g.match ? T.green : T.red} />}
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{g.kind === "face" ? "Face scan" : "Plate scan"} — {g.label}</div>
                <div style={{ fontSize: 11.5, color: T.muted }}>{g.sub} · {g.confidence}% confidence · {g.time}</div>
              </div>
              <HardButton bg={T.green} color={T.white} small onClick={() => approveGate(g)}><Check size={13} /> Approve</HardButton>
              <HardButton bg={T.white} color={T.red} small onClick={() => rejectGate(g)}><X size={13} /> Reject</HardButton>
            </HardCard>
          ))}
        </div>
      </div>
    );
  }

  function Visitors() {
    return (
      <div>
        <SectionHeader title="Visitor & Delivery Log" sub={`${visitorLog.length} entries today`} />
        <Toolbar placeholder="Search by name or flat" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Table columns={["Name", "Flat", "Purpose", "Entry", "Exit", "Status", ""]} rows={filteredVisitors}
          renderRow={(v) => (
            <tr key={v.id}>
              <td style={{ ...td, fontWeight: 600 }}>{v.name}</td>
              <td style={td}>{v.flat}</td><td style={td}>{v.purpose}</td>
              <td style={td}>{v.entry}</td><td style={td}>{v.exit}</td>
              <td style={td}><StickerPill status={v.status} /></td>
              <td style={td}>{v.status === "In" && <HardButton small bg={T.white} onClick={() => logExit(v.id)}>Log exit</HardButton>}</td>
            </tr>
          )} />
      </div>
    );
  }

  function Residents() {
    return (
      <div>
        <SectionHeader title="Resident Directory" sub={`${residents.length} flats on record`} />
        <Toolbar placeholder="Search by flat number or name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <InlineAddRow buttonLabel="Add resident" fields={[
          { key: "flat", label: "Flat no.", required: true },
          { key: "name", label: "Name", required: true },
          { key: "type", label: "Owner / Tenant" },
        ]} onAdd={addResident} />
        <Table columns={["Flat", "Name", "Type", "Contact", "Vehicle", "Face ID", "Consent"]} rows={filteredResidents}
          renderRow={(r, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{r.flat}</td>
              <td style={td}>{r.name}</td><td style={td}>{r.type}</td>
              <td style={td}><div style={{ display: "flex", alignItems: "center", gap: 6, color: T.muted }}><Phone size={12} /> {r.contact}</div></td>
              <td style={td}>{r.plate}</td>
              <td style={td}><StickerPill status={r.face} /></td>
              <td style={td}><StickerPill status={r.consent} /></td>
            </tr>
          )} />
        <p style={{ fontSize: 11.5, color: T.muted, marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <ShieldCheck size={13} /> Flats marked "Pending" consent are excluded from facial matching under DPDP.
        </p>
      </div>
    );
  }

  function Billing() {
    return (
      <div>
        <SectionHeader title="Billing & Dues" sub={billingRows[0]?.month || "Current cycle"} action={<HardButton onClick={generateNextCycle}><Plus size={14} /> Generate next cycle's bills</HardButton>} />
        <HardCard style={{ padding: "12px 16px", marginBottom: 16, background: T.mint, fontSize: 12.5, lineHeight: 1.55 }}>
          Overdue dues accrue interest at <strong>21% p.a.</strong> GST at 18% applies only where monthly maintenance exceeds <strong>₹7,500 per member</strong> — recalculated live below.
        </HardCard>
        <Table columns={["Flat", "Cycle", "Base", "Interest", "GST", "Total due", "Receipt", "Status", ""]} rows={billingRows}
          renderRow={(d, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{d.flat}</td>
              <td style={td}>{d.month}</td>
              <td style={td}>₹{d.amount.toLocaleString("en-IN")}</td>
              <td style={td}>{d.interest ? `₹${d.interest} (${d.daysOverdue}d)` : "—"}</td>
              <td style={td}>{d.gstApplicable ? `₹${d.gst}` : "N/A"}</td>
              <td style={{ ...td, fontWeight: 700 }}>₹{d.total.toLocaleString("en-IN")}</td>
              <td style={td}>{d.receipt !== "—" ? <span style={{ fontFamily: mono, fontSize: 12 }}>{d.receipt}</span> : "—"}</td>
              <td style={td}><StickerPill status={d.status} /></td>
              <td style={td}>{d.status !== "Paid" && <HardButton small bg={T.white} onClick={() => recordReceipt(d.flat)}><Receipt size={12} /> Record receipt</HardButton>}</td>
            </tr>
          )} />
      </div>
    );
  }

  function Accounts() {
    return (
      <div>
        <SectionHeader title="Accounts & Expenses" sub="Every rupee in and out of the society account" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
          <HardCard style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.green, fontSize: 10.5, fontWeight: 700, fontFamily: mono }}><TrendingUp size={14} /> COLLECTED THIS CYCLE</div>
            <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 700, marginTop: 8 }}>₹{stats.totalCollected.toLocaleString("en-IN")}</div>
          </HardCard>
          <HardCard style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.red, fontSize: 10.5, fontWeight: 700, fontFamily: mono }}><TrendingDown size={14} /> TOTAL EXPENSES</div>
            <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 700, marginTop: 8 }}>₹{stats.totalExpenses.toLocaleString("en-IN")}</div>
          </HardCard>
          <HardCard style={{ padding: 16, background: stats.netBalance >= 0 ? T.mint : "#FBE3DF" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.indigo, fontSize: 10.5, fontWeight: 700, fontFamily: mono }}><Landmark size={14} /> SOCIETY BALANCE</div>
            <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 700, marginTop: 8 }}>₹{stats.netBalance.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Opening ₹{OPENING_BALANCE.toLocaleString("en-IN")} + collections − expenses</div>
          </HardCard>
        </div>

        <SectionHeader title="Log an expense" />
        <InlineAddRow buttonLabel="Add expense" fields={[
          { key: "category", label: "Category", type: "select", options: EXPENSE_CATEGORIES },
          { key: "vendor", label: "Paid to (vendor / staff)", required: true },
          { key: "amount", label: "Amount (₹)", type: "number", required: true },
          { key: "mode", label: "Cash / Online / Cheque" },
          { key: "note", label: "Note", flex: 1 },
        ]} onAdd={addExpense} />

        <SectionHeader title="Spend by category" />
        <HardCard style={{ padding: 16, marginBottom: 20 }}>
          {expensesByCategory.length === 0 && <div style={{ fontSize: 13, color: T.muted }}>No expenses logged yet.</div>}
          {expensesByCategory.map(([cat, amt]) => (
            <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0" }}>
              <div style={{ width: 140, fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>{cat}</div>
              <div style={{ flex: 1, background: "#EDE4CE", borderRadius: 6, height: 10, overflow: "hidden" }}>
                <div style={{ width: `${totalExpenses ? Math.round((amt / totalExpenses) * 100) : 0}%`, background: T.indigo, height: "100%" }} />
              </div>
              <div style={{ width: 90, textAlign: "right", fontFamily: mono, fontSize: 12.5, flexShrink: 0 }}>₹{amt.toLocaleString("en-IN")}</div>
            </div>
          ))}
        </HardCard>

        <SectionHeader title="Expense ledger" sub={`${expenses.length} entries`} />
        <Table columns={["Date", "Category", "Paid to", "Amount", "Mode", "Note", ""]} rows={expenses}
          renderRow={(e) => (
            <tr key={e.id}>
              <td style={td}>{e.date}</td>
              <td style={td}><Tag>{e.category}</Tag></td>
              <td style={{ ...td, fontWeight: 600 }}>{e.vendor}</td>
              <td style={{ ...td, fontWeight: 700 }}>₹{e.amount.toLocaleString("en-IN")}</td>
              <td style={td}>{e.mode}</td>
              <td style={{ ...td, color: T.muted, whiteSpace: "normal", maxWidth: 220 }}>{e.note || "—"}</td>
              <td style={td}><HardButton small bg={T.white} color={T.red} onClick={() => removeExpense(e.id)}><Trash2 size={12} /></HardButton></td>
            </tr>
          )} />
      </div>
    );
  }

  const complaintStages = ["Reported", "Assigned", "In progress", "Resolved"];
  function ComplaintStages({ current, onAdvance }) {
    return (
      <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 4 }}>
        {complaintStages.map((s, i) => (
          <React.Fragment key={s}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 64 }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${T.ink}`, background: i <= current ? T.green : T.white, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {i <= current && <Check size={11} color={T.white} />}
              </div>
              <span style={{ fontSize: 9.5, textAlign: "center", color: i <= current ? T.ink : T.muted, fontWeight: i === current ? 700 : 500, fontFamily: mono }}>{s}</span>
            </div>
            {i < complaintStages.length - 1 && <div style={{ flex: 1, height: 2.5, background: i < current ? T.green : "#DDD3B8", marginBottom: 14 }} />}
          </React.Fragment>
        ))}
        {current < 3 && <HardButton small onClick={onAdvance} style={{ marginBottom: 14, marginLeft: 8 }}>Advance <ArrowRight size={12} /></HardButton>}
      </div>
    );
  }

  function Complaints() {
    return (
      <div>
        <SectionHeader title="Complaints & Helpdesk" sub={`${complaints.length} tickets · tracked from report to resolution`} />
        <InlineAddRow buttonLabel="Log complaint" fields={[
          { key: "title", label: "What's wrong?", required: true, flex: 1 },
          { key: "flat", label: "Flat / area" },
          { key: "priority", label: "Priority (Low/Medium/High)" },
        ]} onAdd={addComplaint} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {complaints.map((c) => (
            <HardCard key={c.id} style={{ padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{c.id} · {c.title}</div>
                  <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{c.flat} · Priority: {c.priority} · Assigned: {c.assignee}</div>
                </div>
                <StickerPill status={complaintStages[c.stage]} />
              </div>
              <ComplaintStages current={c.stage} onAdvance={() => advanceComplaint(c.id)} />
            </HardCard>
          ))}
        </div>
      </div>
    );
  }

  function MaintenanceLog() {
    return (
      <div>
        <SectionHeader title="Maintenance Log" sub="Service history and renewal alerts for shared assets" />
        <InlineAddRow buttonLabel="Log service" fields={[
          { key: "asset", label: "Asset / service", required: true, flex: 1 },
          { key: "nextDue", label: "Next due (e.g. Dec 2026)" },
        ]} onAdd={addMaintenance} />
        <Table columns={["Asset / Service", "Last done", "Next due", "Status"]} rows={maintenanceLog}
          renderRow={(m, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{m.asset}</td>
              <td style={td}>{m.lastDone}</td>
              <td style={td}>{m.nextDue}</td>
              <td style={td}><StickerPill status={m.status} /></td>
            </tr>
          )} />
      </div>
    );
  }

  function Staff() {
    return (
      <div>
        <SectionHeader title="Staff & Roles" sub="Approve household staff, guards, and vendors — assign their role" />
        <InlineAddRow buttonLabel="Add staff" fields={[
          { key: "name", label: "Name", required: true },
          { key: "type", label: "Type (Maid/Electrician/Guard...)" },
          { key: "flat", label: "Linked flat" },
        ]} onAdd={addStaff} />
        <Table columns={["Name", "Type", "Linked to", "Role", "Status", ""]} rows={staffRoles}
          renderRow={(s, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
              <td style={td}>{s.type}</td>
              <td style={td}>{s.flat}</td>
              <td style={td}>{s.role !== "—" ? <Tag>{s.role}</Tag> : "—"}</td>
              <td style={td}><StickerPill status={s.status} /></td>
              <td style={td}>{s.status === "Pending" && <HardButton small bg={T.green} color={T.white} onClick={() => approveStaff(s.name)}><Check size={12} /> Approve</HardButton>}</td>
            </tr>
          )} />
      </div>
    );
  }

  function Amenities() {
    const [newVenue, setNewVenue] = useState("");
    return (
      <div>
        <SectionHeader title="Venues & Amenities" sub="Define what your society offers, then track booking requests against it" />
        <HardCard style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ fontFamily: sans, fontWeight: 700, fontSize: 14.5, marginBottom: 10 }}>Your venues ({venues.length})</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {venues.map((v) => (
              <span key={v} style={{ display: "flex", alignItems: "center", gap: 6, background: T.cream, border: `2px solid ${T.ink}`, borderRadius: 999, padding: "6px 8px 6px 14px", fontSize: 12.5, fontWeight: 600 }}>
                {v}
                <button onClick={() => removeVenue(v)} style={{ border: "none", background: T.white, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <Trash2 size={11} color={T.red} />
                </button>
              </span>
            ))}
            {venues.length === 0 && <span style={{ fontSize: 12.5, color: T.muted }}>No venues yet — add your first one.</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="e.g. Rooftop Garden" value={newVenue} onChange={(e) => setNewVenue(e.target.value)}
              style={{ border: `2px solid ${T.ink}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: sans, flex: 1, maxWidth: 240 }} />
            <HardButton small onClick={() => { addVenue(newVenue); setNewVenue(""); }}><Plus size={13} /> Add venue</HardButton>
          </div>
        </HardCard>

        <SectionHeader title="Booking requests" sub="Access requests and payment status" />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {amenities.map((a, i) => (
            <HardCard key={i} style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: T.cyan, border: `2px solid ${T.ink}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Building2 size={15} color={T.ink} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{a.name} — {a.requester}</div>
                <div style={{ fontSize: 11.5, color: T.muted }}>{a.slot} · Payment: {a.payment}</div>
              </div>
              <StickerPill status={a.status} />
              {a.status === "Pending" && <HardButton small bg={T.green} color={T.white} onClick={() => approveAmenity(i)}><Check size={12} /> Approve</HardButton>}
            </HardCard>
          ))}
          {amenities.length === 0 && <HardCard style={{ padding: 16, fontSize: 13.5, color: T.muted }}>No booking requests yet.</HardCard>}
        </div>
      </div>
    );
  }

  function UserCodes() {
    return (
      <div>
        <SectionHeader title="New Users & Code Generation" sub="Each code carries a pre-assigned role for onboarding" action={<HardButton onClick={generateCode}><Plus size={14} /> Generate code</HardButton>} />
        <Table columns={["Code", "Role", "Linked to", "Status"]} rows={userCodes}
          renderRow={(c, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 700, fontFamily: mono }}>{c.code}</td>
              <td style={td}><Tag>{c.role}</Tag></td>
              <td style={td}>{c.flat}</td>
              <td style={td}><StickerPill status={c.status} /></td>
            </tr>
          )} />
      </div>
    );
  }

  function Announcements() {
    return (
      <div>
        <SectionHeader title="Announcements" sub="Visible to all residents via app & WhatsApp" />
        <InlineAddRow buttonLabel="Post notice" fields={[
          { key: "title", label: "Title", required: true, flex: 1 },
          { key: "body", label: "Details", flex: 1 },
        ]} onAdd={addAnnouncement} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {announcements.map((a, i) => (
            <HardCard key={i} style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{a.title}</span>
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 12, fontFamily: mono }}>{a.date}</span>
              </div>
              <p style={{ fontSize: 12.5, color: T.muted, margin: "6px 0 0" }}>{a.body}</p>
            </HardCard>
          ))}
        </div>
      </div>
    );
  }

  const content = useMemo(() => {
    switch (active) {
      case "gateai": return <GateAI />;
      case "visitors": return <Visitors />;
      case "residents": return <Residents />;
      case "billing": return <Billing />;
      case "accounts": return <Accounts />;
      case "complaints": return <Complaints />;
      case "maintenance": return <MaintenanceLog />;
      case "staff": return <Staff />;
      case "amenities": return <Amenities />;
      case "codes": return <UserCodes />;
      case "announcements": return <Announcements />;
      default: return <Overview />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, gateAIFeed, visitorLog, residents, dues, complaints, maintenanceLog, staffRoles, userCodes, venues, amenities, announcements, expenses, plan, search]);

  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div style={{ fontFamily: sans, minHeight: "100vh", color: T.ink, background: T.cream, display: "flex" }}>
      <style>{FONT_IMPORT}{`
        .navscroll::-webkit-scrollbar{ display:none; }
        .sidebar{ transition: width .2s ease; }
        .sidebar-label{ transition: opacity .15s ease; white-space:nowrap; }
      `}</style>

      {/* ---------- SIDEBAR ---------- */}
      <div className="sidebar" style={{
        width: sidebarOpen ? 232 : 68, flexShrink: 0, minHeight: "100vh", background: T.ink,
        borderRight: `3px solid ${T.ink}`, display: "flex", flexDirection: "column", position: "sticky", top: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: sidebarOpen ? "18px 16px" : "18px 0", justifyContent: sidebarOpen ? "flex-start" : "center" }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", background: T.yellow, border: `2px solid ${T.white}`, flexShrink: 0 }} />
          {sidebarOpen && <span className="sidebar-label" style={{ fontFamily: sans, fontWeight: 700, color: T.white, fontSize: 15.5 }}>Bellboy ERP</span>}
        </div>
        {sidebarOpen && (
          <div style={{ padding: "0 16px 14px", borderBottom: "1px solid rgba(255,255,255,0.12)", marginBottom: 10 }}>
            <div style={{ fontSize: 10.5, color: "#8890BE", fontFamily: mono }}>{societyName.toUpperCase()}</div>
            <Tag bg={T.yellow}>₹{plan} PLAN</Tag>
          </div>
        )}
        <div className="navscroll" style={{ flex: 1, overflowY: "auto", padding: "6px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {NAV.map((n) => {
            const isActive = active === n.id;
            return (
              <button key={n.id} onClick={() => setActive(n.id)} title={n.label}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: sidebarOpen ? "10px 12px" : "10px 0",
                  justifyContent: sidebarOpen ? "flex-start" : "center", borderRadius: 10, border: "none",
                  background: isActive ? T.yellow : "transparent", color: isActive ? T.ink : "#C7CCF0",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: sans, width: "100%",
                }}>
                <n.icon size={16} style={{ flexShrink: 0 }} />
                {sidebarOpen && <span className="sidebar-label">{n.label}</span>}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: sidebarOpen ? "flex-start" : "center" }}>
            <div style={{ width: 26, height: 26, borderRadius: 99, background: T.cyan, border: `2px solid ${T.white}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials || "AD"}</div>
            {sidebarOpen && <span className="sidebar-label" style={{ fontSize: 12, color: T.white }}>{adminName}</span>}
          </div>
          {onLogout && (
            <button onClick={onLogout} title="Log out" style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", color: "#B9C0E8", cursor: "pointer", fontSize: 12, fontFamily: sans, justifyContent: sidebarOpen ? "flex-start" : "center", padding: sidebarOpen ? "6px 4px" : "6px 0" }}>
              <LogOut size={14} /> {sidebarOpen && <span className="sidebar-label">Log out</span>}
            </button>
          )}
          <button onClick={() => setSidebarOpen((o) => !o)} style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: sidebarOpen ? "flex-start" : "center", background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, color: "#C7CCF0", cursor: "pointer", fontSize: 11.5, fontFamily: sans, padding: "8px" }}>
            {sidebarOpen ? <ChevronsLeft size={14} /> : <ChevronsRight size={14} />}
            {sidebarOpen && <span className="sidebar-label">Collapse</span>}
          </button>
        </div>
      </div>

      {/* ---------- MAIN ---------- */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "14px 20px", background: T.white, borderBottom: `3px solid ${T.ink}` }}>
          <Bell size={17} color={T.ink} />
        </div>
        <div style={{ ...dotBg, padding: "22px 20px 60px" }}>{content}</div>
      </div>
    </div>
  );
}